/**
 * GameLoop - Fixed Timestep Game Loop
 * 
 * Provides a deterministic update/render loop with fixed physics timestep
 * and interpolation for smooth rendering.
 */

/** Fixed timestep in seconds (60 Hz) */
export const FIXED_TIMESTEP = 1 / 60;

/** Maximum number of physics steps per frame (prevents spiral of death) */
const MAX_STEPS_PER_FRAME = 5;

/** Callback types for game loop events */
export interface GameLoopCallbacks {
    /** Called at fixed intervals for physics/logic updates */
    update: (dt: number) => void;
    /** Called every frame with interpolation alpha for smooth rendering */
    render: (alpha: number) => void;
}

/**
 * GameLoop - Manages the main game timing
 * 
 * Uses a fixed timestep for deterministic physics simulation while
 * allowing variable frame rate rendering with interpolation.
 */
export class GameLoop {
    private callbacks: GameLoopCallbacks;
    private running = false;
    private accumulator = 0;
    private lastTime = 0;
    private animationFrameId: number | null = null;

    // Performance tracking
    private frameCount = 0;
    private fpsAccumulator = 0;
    private currentFps = 0;

    constructor(callbacks: GameLoopCallbacks) {
        this.callbacks = callbacks;
    }

    /** Start the game loop */
    start(): void {
        if (this.running) return;
        
        this.running = true;
        this.lastTime = performance.now();
        this.accumulator = 0;
        this.tick();
    }

    /** Stop the game loop */
    stop(): void {
        this.running = false;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    /** Check if the loop is running */
    isRunning(): boolean {
        return this.running;
    }

    /** Get current FPS */
    getFps(): number {
        return this.currentFps;
    }

    /** Main loop tick */
    private tick = (): void => {
        if (!this.running) return;

        const currentTime = performance.now();
        const frameTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
        this.lastTime = currentTime;

        // Track FPS
        this.frameCount++;
        this.fpsAccumulator += frameTime;
        if (this.fpsAccumulator >= 1.0) {
            this.currentFps = Math.round(this.frameCount / this.fpsAccumulator);
            this.frameCount = 0;
            this.fpsAccumulator = 0;
        }

        // Clamp frame time to prevent spiral of death
        // If the frame took too long, we'll just slow down the game
        const clampedFrameTime = Math.min(frameTime, FIXED_TIMESTEP * MAX_STEPS_PER_FRAME);
        this.accumulator += clampedFrameTime;

        // Run fixed timestep updates
        let steps = 0;
        while (this.accumulator >= FIXED_TIMESTEP && steps < MAX_STEPS_PER_FRAME) {
            this.callbacks.update(FIXED_TIMESTEP);
            this.accumulator -= FIXED_TIMESTEP;
            steps++;
        }

        // Calculate interpolation alpha for smooth rendering
        // This represents how far we are between physics steps
        const alpha = this.accumulator / FIXED_TIMESTEP;

        // Render with interpolation
        this.callbacks.render(alpha);

        // Schedule next frame
        this.animationFrameId = requestAnimationFrame(this.tick);
    };
}
